/**
 * Хуки данных экрана «Проживания» менеджера (Tanstack Query).
 *
 * Источник списка — GET /v1/rooms/calendar?from=&to= (per-room occupancies:
 * stay_id, from, to, status, guest_id). Это единственный bulk-источник
 * проживаний по тенанту (GET /v1/stays требует ?room=). Карточка —
 * GET /v1/stays/{id}. Lifecycle-мутации — POST confirm/check-in/check-out/cancel.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelStay,
  checkInStay,
  checkOutStay,
  confirmStay,
  createStay,
  getRoomsCalendar,
  getStay,
  listStays,
} from '@hsc/api-client';
import type {
  CalendarResponse,
  CreateStayInput,
  StayListParams,
  StayListResponse,
  StayResponse,
} from '@hsc/types';

export function useStaysCalendar(from: string, to: string) {
  return useQuery<CalendarResponse>({
    queryKey: ['stays', 'calendar', from, to],
    queryFn: () => getRoomsCalendar(from, to),
  });
}

/**
 * Серверный список проживаний для экрана «Проживания» (GET /v1/stays):
 * фильтр по статусу + сортировка по номеру/заезду/выезду + OFFSET-пагинация.
 * keepPreviousData — при смене страницы/сортировки старая страница остаётся
 * видимой до прихода новой (без мигания скелетона).
 */
export function useStaysList(params: StayListParams) {
  return useQuery<StayListResponse>({
    queryKey: ['stays', 'list', params],
    queryFn: () => listStays(params),
    placeholderData: keepPreviousData,
  });
}

export function useStay(id: string | null) {
  return useQuery<StayResponse>({
    queryKey: ['stays', 'detail', id],
    queryFn: () => getStay(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Создание брони из формы менеджера «+ Бронь» на шахматке (POST /v1/stays).
 * По успеху инвалидирует календарь — новая полоса появляется в сетке.
 */
export function useCreateStay() {
  const qc = useQueryClient();
  return useMutation<StayResponse, Error, CreateStayInput>({
    mutationFn: (input) => createStay(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      void qc.invalidateQueries({ queryKey: ['stays'] });
    },
  });
}

type Action = 'confirm' | 'check-in' | 'check-out' | 'cancel';

export function useStayLifecycle() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; action: Action; reason?: string }>({
    mutationFn: ({ id, action, reason }) => {
      switch (action) {
        case 'confirm':
          return confirmStay(id);
        case 'check-in':
          return checkInStay(id);
        case 'check-out':
          return checkOutStay(id);
        case 'cancel':
          return cancelStay(id, reason ?? '');
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stays'] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });
}
